/**
 * Shared date formatting utilities.
 *
 * Used by DataSourceDetail, DataProductDetail, TemporalRangeBar, and any
 * other component that needs consistent human-readable date strings.
 */

/**
 * Formats an ISO date string into "Mon DD, YYYY" (e.g. "Jan 5, 2024").
 * Returns null for falsy or invalid input.
 */
export function formatDate(dateStr) {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

/**
 * Formats an ISO date string into a relative time string when recent,
 * falling back to formatDate for older dates.
 *
 * Returns "Today", "Yesterday", "X days ago", "X weeks ago", or the
 * formatted absolute date.
 */
export function formatRelativeDate(dateStr) {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return formatDate(dateStr);
}

/**
 * Formats an ISO date string into "Mon YYYY" (e.g. "Jan 2024").
 * Useful for timeline/range displays where the day is not important.
 * Returns null for falsy or invalid input.
 */
export function formatMonthYear(dateStr) {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
}
